import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useLocations, Location } from '@/hooks/use-locations';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface LocationContextType {
  currentLocation: Location | null;
  availableLocations: Location[];
  isLoading: boolean;
  error: string | null;
  canSwitchLocation: boolean;
  setCurrentLocation: (location: Location) => void;
  refreshLocations: () => void;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

interface LocationProviderProps {
  children: ReactNode;
}

export const LocationProvider: React.FC<LocationProviderProps> = ({ children }) => {
  const { companyId, user, userRole } = useAuth();
  const { locations, loading, error, getDefaultLocation, getActiveLocations, refetch } = useLocations();
  const { toast } = useToast();

  const [currentLocation, setCurrentLocationState] = useState<Location | null>(null);
  const [userAssignedLocationId, setUserAssignedLocationId] = useState<string | null>(null);
  const [assignedLocationLoaded, setAssignedLocationLoaded] = useState(false);

  const isAdmin = userRole === 'admin';
  const canSwitchLocation = isAdmin;

  // Fetch the user's assigned location_id from user_profiles
  useEffect(() => {
    const fetchUserLocation = async () => {
      if (!user?.id) {
        setAssignedLocationLoaded(true);
        return;
      }
      try {
        const { data, error: fetchError } = await supabase
          .from('user_profiles')
          .select('location_id')
          .eq('id', user.id)
          .single();

        if (!fetchError && data) {
          setUserAssignedLocationId(data.location_id);
        }
      } catch (err) {
        console.error('Error fetching user location:', err);
      } finally {
        setAssignedLocationLoaded(true);
      }
    };

    fetchUserLocation();
  }, [user?.id]);

  // Initialize current location when locations are loaded
  useEffect(() => {
    if (locations.length === 0 || !assignedLocationLoaded) return;
    if (currentLocation) return;

    const activeLocations = getActiveLocations();

    // Non-admin: lock to assigned branch
    if (!isAdmin && userAssignedLocationId) {
      const assignedLocation = activeLocations.find(loc => loc.id === userAssignedLocationId);
      if (assignedLocation) {
        setCurrentLocationState(assignedLocation);
        return;
      }
    }

    // Admin: try localStorage first
    if (isAdmin) {
      const savedLocationId = localStorage.getItem('currentLocationId');
      if (savedLocationId) {
        const savedLocation = activeLocations.find(loc => loc.id === savedLocationId);
        if (savedLocation) {
          setCurrentLocationState(savedLocation);
          return;
        }
      }
    }

    // Fallback: default location, then first active
    const defaultLocation = getDefaultLocation();
    if (defaultLocation) {
      setCurrentLocationState(defaultLocation);
      if (isAdmin) localStorage.setItem('currentLocationId', defaultLocation.id);
    } else if (activeLocations.length > 0) {
      setCurrentLocationState(activeLocations[0]);
      if (isAdmin) localStorage.setItem('currentLocationId', activeLocations[0].id);
    }
  }, [locations, currentLocation, assignedLocationLoaded, userAssignedLocationId, isAdmin, getDefaultLocation, getActiveLocations]);

  // Clear current location when company changes
  useEffect(() => {
    if (companyId) {
      setCurrentLocationState(null);
      setAssignedLocationLoaded(false);
      localStorage.removeItem('currentLocationId');
    }
  }, [companyId]);

  const setCurrentLocation = (location: Location) => {
    if (!canSwitchLocation) {
      toast({
        title: "Uyarı",
        description: "Şube değiştirme yetkiniz bulunmamaktadır",
        variant: "destructive",
      });
      return;
    }

    if (!location.is_active) {
      toast({
        title: "Uyarı",
        description: "Pasif lokasyon seçilemez",
        variant: "destructive",
      });
      return;
    }

    setCurrentLocationState(location);
    localStorage.setItem('currentLocationId', location.id);

    toast({
      title: "Lokasyon Değiştirildi",
      description: `${location.name} lokasyonuna geçiş yapıldı`,
    });
  };

  const refreshLocations = () => {
    if (refetch) {
      refetch();
    }
  };

  const value: LocationContextType = {
    currentLocation,
    availableLocations: getActiveLocations(),
    isLoading: loading || !assignedLocationLoaded,
    error,
    canSwitchLocation,
    setCurrentLocation,
    refreshLocations,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

// Custom hook for location-aware data filtering
export const useLocationFilter = () => {
  const { currentLocation } = useLocation();

  const getLocationFilter = () => {
    return currentLocation ? { location_id: currentLocation.id } : {};
  };

  const getCurrentLocationId = () => {
    return currentLocation?.id || null;
  };

  return {
    locationFilter: getLocationFilter(),
    currentLocationId: getCurrentLocationId(),
    hasLocation: !!currentLocation
  };
};
